import type { FacetOption } from "@/components/marketplace/listing/types"
import { buildRoute } from "@/lib/routes"
import { listingBaseQuery } from "@/lib/services/search/search"
import type { SearchQuery } from "@/lib/types/search"

const flatten = (categoryOptions: FacetOption[]): FacetOption[] => categoryOptions.flatMap((option) => [option, ...flatten(option.children ?? [])])

const descendantsOf = (id: string, options: FacetOption[]): string[] => flatten(options.find((option) => option.value === id)?.children ?? []).map((option) => option.value)

const pageCategories = (baseIds: string[], options: FacetOption[]): Set<string> => new Set([...baseIds, ...baseIds.flatMap((id) => descendantsOf(id, options))])

const categoryRoute = (categories: string[], options: FacetOption[]): string => {
  const [only] = categories
  const handle = categories.length === 1 ? options.find((option) => option.value === only)?.handle : undefined
  return handle ? buildRoute.category(handle) : buildRoute.search({ categories }, listingBaseQuery())
}

export const categoryExitRoute = (baseQuery: SearchQuery, updates: Partial<SearchQuery>, categoryOptions: FacetOption[] = []): string | null => {
  if (baseQuery.categories.length === 0 || updates.categories === undefined) return null
  const options = flatten(categoryOptions)
  const scope = pageCategories(baseQuery.categories, options)
  if (updates.categories.length > 0 && updates.categories.every((id) => scope.has(id))) return null
  return categoryRoute(updates.categories, options)
}

const sameSelection = (current: string[], next: string[]): boolean => current.length === next.length && current.every((id) => next.includes(id))

const isDrillDown = (current: string[], next: string[], options: FacetOption[]): boolean =>
  next.length > 0 && (current.length === 0 || next.every((id) => current.some((parent) => descendantsOf(parent, options).includes(id))))

const isClimb = (current: string[], next: string[], options: FacetOption[]): boolean => next.every((id) => current.some((child) => descendantsOf(id, options).includes(child)))

const hasResults = (ids: string[], options: FacetOption[]): boolean =>
  ids.every((id) => {
    const count = options.find((option) => option.value === id)?.count
    return count === undefined || count > 0
  })

export const searchExitRoute = (query: SearchQuery, updates: Partial<SearchQuery>, categoryOptions: FacetOption[] = []): string | null => {
  if (!query.text || updates.categories === undefined) return null
  const options = flatten(categoryOptions)
  const next = updates.categories
  if (sameSelection(query.categories, next) || isClimb(query.categories, next, options) || hasResults(next, options)) return null
  return categoryRoute(next, options)
}

export const categorySwitchUpdates = (query: SearchQuery, updates: Partial<SearchQuery>, categoryOptions: FacetOption[] = []): Partial<SearchQuery> => {
  if (updates.categories === undefined) return updates
  if (sameSelection(query.categories, updates.categories) || isDrillDown(query.categories, updates.categories, flatten(categoryOptions))) return updates
  const { attributes, priceMin, priceMax, inStockOnly, sort } = listingBaseQuery()
  return { ...updates, attributes, priceMin, priceMax, inStockOnly, sort }
}
